import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Input, InputNumber, message, Space, Table } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { POAmendment, PurchaseOrder } from "../../api-models";

interface DraftLine {
  poItemId: number;
  itemCode: string;
  itemName: string;
  currentQty: number;
  deltaQty: number;
}

// Creates or views a PO Amendment (spec §14.1/§31.20) — the approved escape from GRN/MRN's
// outright over-receipt rejection. New amendments only add quantity to existing PO lines;
// price overrides can be added later via the API if a project needs them.
export default function POAmendmentDetailPage() {
  const { poId, id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();

  const isNew = id === "new";
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [reason, setReason] = useState("");
  const [amendment, setAmendment] = useState<POAmendment | null>(null);

  useEffect(() => {
    if (isNew) {
      request<PurchaseOrder>({ url: `/pos/${poId}` }).then((res) => {
        setPo(res);
        setLines(
          (res.items ?? []).map((it) => ({
            poItemId: it.id,
            itemCode: it.item?.code ?? String(it.itemId),
            itemName: it.item?.name ?? "",
            currentQty: it.qty,
            deltaQty: 0,
          })),
        );
      });
    } else {
      request<POAmendment>({ url: `/amendments/${id}` }).then(setAmendment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, poId]);

  const canWrite = hasAnyGrant(user, "PO.Write");

  async function save() {
    const changed = lines.filter((l) => l.deltaQty !== 0).map((l) => ({ poItemId: l.poItemId, deltaQty: l.deltaQty }));
    if (!reason || changed.length === 0) {
      message.error("A reason and at least one quantity change are required.");
      return;
    }
    const created = await request<POAmendment>({ method: "POST", url: `/pos/${poId}/amendments`, data: { reason, lines: changed } });
    message.success("Amendment drafted.");
    navigate(`/procurement/pos/${poId}/amendments/${created.id}`, { replace: true });
  }

  async function submit() {
    await request({ method: "POST", url: `/amendments/${id}/submit` });
    message.success("Submitted for approval.");
    request<POAmendment>({ url: `/amendments/${id}` }).then(setAmendment);
  }

  if (isNew) {
    return (
      <div>
        <PageHeader
          title="New PO Amendment"
          subtitle={po ? po.docNo : undefined}
          extra={
            canWrite && (
              <Button type="primary" onClick={save} loading={loading} disabled={!po} data-tour="amendment-save">
                Save
              </Button>
            )
          }
        />
        <Card size="small" title="Reason" style={{ marginBottom: 16 }}>
          <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-tour="amendment-reason" />
        </Card>
        <Card size="small" title="Quantity changes" data-tour="amendment-lines">
          <Table
            rowKey="poItemId"
            dataSource={lines}
            pagination={false}
            columns={[
              { title: "Code", dataIndex: "itemCode" },
              { title: "Item", dataIndex: "itemName" },
              { title: "Current qty", dataIndex: "currentQty" },
              {
                title: "Delta qty",
                render: (_, r) => (
                  <InputNumber
                    value={r.deltaQty}
                    onChange={(v) => setLines((prev) => prev.map((l) => (l.poItemId === r.poItemId ? { ...l, deltaQty: v ?? 0 } : l)))}
                  />
                ),
              },
              { title: "New qty", render: (_, r) => r.currentQty + r.deltaQty },
            ]}
          />
        </Card>
      </div>
    );
  }

  if (!amendment) return null;

  return (
    <div>
      <PageHeader
        title={amendment.docNo}
        extra={
          <Space>
            <span data-tour="doc-status">
              <DocumentStatusTag status={amendment.status} />
            </span>
            {amendment.status === "DRAFT" && canWrite && (
              <Button type="primary" onClick={submit} loading={loading} data-tour="amendment-submit">
                Submit for approval
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" title="Reason" style={{ marginBottom: 16 }}>
        {amendment.reason}
      </Card>
      <Card size="small" title="Line changes">
        <Table
          rowKey="id"
          dataSource={amendment.lines ?? []}
          pagination={false}
          columns={[
            { title: "PO item", dataIndex: "poItemId" },
            { title: "Delta qty", dataIndex: "deltaQty" },
          ]}
        />
      </Card>
      <ApprovalTimeline
        docType="PO_AMENDMENT"
        docId={amendment.id}
        refreshKey={amendment.status}
        onActed={() => request<POAmendment>({ url: `/amendments/${id}` }).then(setAmendment)}
      />
    </div>
  );
}
